import { useMemo, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { Button, Input } from '@/components/ui';

/**
 * QA-80 — 고시 항목(라벨→값) 행 editor. 순서 보존 Record<string,string> 를 다룬다.
 * 상품정보고시 템플릿 폼이 사용. NoticeSpecEditor 의 행 UI 와 동일한 패턴(추가/삭제/수정).
 *
 * 키(라벨)는 onBlur 로 반영(편집 중 중복키 깜빡임 방지), 값은 onChange 로 즉시 반영.
 * 빈 라벨 행은 저장 시 자동 제거된다(reconstruct).
 */
export interface NoticeSpecItemsEditorProps {
  value: Record<string, string>;
  disabled?: boolean;
  onChange: (next: Record<string, string>) => void;
}

export const NoticeSpecItemsEditor = ({ value, disabled, onChange }: NoticeSpecItemsEditorProps) => {
  const { t } = useTranslation('products');
  const entries = useMemo(() => Object.entries(value), [value]);

  // 값 입력은 onChange 로 즉시 반영하는데, 키마다 onChange→onChange(next)→상위 setItems→재렌더가
  // 일어난다. CJK(중문/일문/한글) IME 는 조합(composition) 중 이 재렌더가 끼면 조합이 깨져 글자가
  // 입력되지 않는다. 조합 중에는 onChange 반영을 보류하고 compositionEnd 에서 1회 커밋한다.
  const composingRef = useRef(false);

  // idx 행의 키를 newKey 로 바꾸고 reconstruct(빈 키 제거, 순서 유지).
  const updateEntry = (idx: number, key: string, val: string) => {
    const next: Record<string, string> = {};
    entries.forEach(([k, v], i) => {
      if (i === idx) {
        if (key.trim()) next[key] = val;
      } else if (k.trim()) {
        next[k] = v;
      }
    });
    onChange(next);
  };

  const removeEntry = (idx: number) => {
    const next: Record<string, string> = {};
    entries.forEach(([k, v], i) => {
      if (i !== idx && k.trim()) next[k] = v;
    });
    onChange(next);
  };

  const addEntry = () => {
    const next: Record<string, string> = { ...value };
    let placeholder = '신규 항목';
    let n = 1;
    while (placeholder in next) {
      n += 1;
      placeholder = `신규 항목 ${n}`;
    }
    next[placeholder] = '';
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      {entries.length === 0 ? (
        <div
          className="flex items-center justify-center rounded-md py-6 text-[13px]"
          style={{
            background: 'var(--surface-0)',
            border: '1px dashed var(--divider-strong)',
            color: 'var(--content-muted)',
          }}
        >
          {t('products.fields.noticeSpecEmpty', '등록된 고시 항목이 없습니다.')}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {entries.map(([key, val], idx) => (
            <li
              key={`${idx}-${key}`}
              className="grid items-center gap-2"
              // 라벨 칼럼을 좁게(96~150px) 잡고 값 칼럼(1fr)에 남은 폭을 몰아준다.
              // minmax(0, …) 로 grid item 기본 min-width:auto 를 무력화해 값 입력이 붕괴하지 않게 한다.
              // (모달 폭은 max-w-2xl 로 확대 — 좁은 max-w-lg 에서 값 칸이 ~30px 로 짜부되던 문제 해소.)
              style={{ gridTemplateColumns: 'minmax(96px, 150px) minmax(0, 1fr) auto' }}
            >
              <Input
                type="text"
                defaultValue={key}
                disabled={disabled}
                placeholder={t('products.fields.noticeSpecKey', '항목명 (예: 원산지)')}
                onBlur={(e) => updateEntry(idx, e.target.value, val)}
              />
              <Input
                type="text"
                defaultValue={val}
                disabled={disabled}
                placeholder={t('products.fields.noticeSpecValue', '값 (예: 대한민국)')}
                onCompositionStart={() => {
                  composingRef.current = true;
                }}
                onCompositionEnd={(e) => {
                  composingRef.current = false;
                  updateEntry(idx, key, e.currentTarget.value);
                }}
                onChange={(e) => {
                  // IME 조합 중에는 재렌더를 보류(조합 깨짐 방지). 조합 종료 시 compositionEnd 에서 커밋.
                  if (composingRef.current) return;
                  updateEntry(idx, key, e.target.value);
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => removeEntry(idx)}
                aria-label={t('products.fields.noticeSpecRemove', '삭제')}
              >
                <X size={14} style={{ color: 'var(--signal-danger)' }} />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div>
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={addEntry}>
          <Plus size={14} />
          {t('products.fields.noticeSpecAdd', '항목 추가')}
        </Button>
      </div>
    </div>
  );
};
